import { extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import { forwardRef, useState, useRef, useImperativeHandle } from 'react';
import { useGeneratedId } from '../../../utils/index.js';
import { Container, Label, TextAreaContainer, Adornment, StyledTextArea, Description } from './styled.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useAutoHeight } from './hooks/useAutoHeight.js';
import { useDescriptionWarning } from './hooks/useDescriptionWarning.js';
import { removeLineBreaks } from './utils/removeLineBreaks.js';

var __rest = undefined && undefined.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
var TextArea = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  var autoId = useGeneratedId('textinput');
  var _props$variant = props.variant,
    variant = _props$variant === void 0 ? 'outline' : _props$variant,
    _props$id = props.id,
    id = _props$id === void 0 ? autoId : _props$id,
    label = props.label,
    ariaDescribedBy = props['aria-describedby'],
    description = props.description,
    _props$stretch = props.stretch,
    stretch = _props$stretch === void 0 ? false : _props$stretch,
    _props$invalid = props.invalid,
    invalid = _props$invalid === void 0 ? false : _props$invalid,
    _props$disabled = props.disabled,
    disabled = _props$disabled === void 0 ? false : _props$disabled,
    appearance = props.appearance,
    adornmentLeft = props.adornmentLeft,
    adornmentRight = props.adornmentRight,
    defaultValue = props.defaultValue,
    value = props.value,
    onChange = props.onChange,
    _props$resize = props.resize,
    resize = _props$resize === void 0 ? 'none' : _props$resize,
    _props$preventLinebre = props.preventLinebreaks,
    preventLinebreaks = _props$preventLinebre === void 0 ? false : _props$preventLinebre,
    _props$autosizeHeight = props.autosizeHeight,
    autosizeHeight = _props$autosizeHeight === void 0 ? false : _props$autosizeHeight,
    _props$minHeight = props.minHeight,
    minHeight = _props$minHeight === void 0 ? 16 : _props$minHeight,
    _props$labelSize = props.labelSize,
    labelSize = _props$labelSize === void 0 ? 'small' : _props$labelSize,
    restProps = __rest(props, ["variant", "id", "label", 'aria-describedby', "description", "stretch", "invalid", "disabled", "appearance", "adornmentLeft", "adornmentRight", "defaultValue", "value", "onChange", "resize", "preventLinebreaks", "autosizeHeight", "minHeight", "labelSize"]);
  var _useState = useState(defaultValue),
    unControlledValue = _useState[0],
    setUncontrolledValue = _useState[1];
  var empty = !unControlledValue && !value; // Note if refactoring: user may modify the value without onChange (autocomplete for example)
  var textAreaRef = useRef(null);
  useImperativeHandle(forwardedRef, function () {
    return textAreaRef.current;
  });
  var _useAutoHeight = useAutoHeight({
      textAreaRef: textAreaRef,
      minHeight: minHeight,
      enabled: autosizeHeight
    }),
    setAutoHeight = _useAutoHeight.setAutoHeight;
  var handleChange = function handleChange(event) {
    if (preventLinebreaks) {
      // eslint-disable-next-line no-param-reassign
      event.target.value = removeLineBreaks(event.target.value);
    }
    if (onChange) {
      onChange(event);
    } else {
      setUncontrolledValue(event.target.value);
    }
    if (autosizeHeight) {
      setAutoHeight();
    }
  };
  var describedBy = ariaDescribedBy || description && id + "-description";
  useDescriptionWarning({
    ariaDescribedBy: ariaDescribedBy,
    description: description
  });
  var textAreaProps = Object.assign(Object.assign({}, restProps), {
    ref: textAreaRef,
    id: id,
    disabled: disabled,
    'aria-describedby': describedBy,
    defaultValue: defaultValue,
    value: value,
    onChange: handleChange,
    $resize: resize,
    $minHeight: minHeight
  });
  return /*#__PURE__*/jsxs(Container, {
    $stretch: stretch,
    children: [label && /*#__PURE__*/jsx(Label, {
      htmlFor: id,
      $invalid: invalid,
      $emptyInput: empty && !adornmentLeft // Consider input non-empty also if there's an adornment, because it occupies the same space as the input text
      ,
      $variant: variant,
      $labelSize: labelSize,
      children: label
    }), /*#__PURE__*/jsxs(TextAreaContainer, {
      $variant: variant,
      $invalid: invalid,
      $appearance: appearance,
      children: [adornmentLeft && /*#__PURE__*/jsx(Adornment, {
        children: adornmentLeft
      }), /*#__PURE__*/jsx(StyledTextArea, _extends({}, textAreaProps)), adornmentRight && /*#__PURE__*/jsx(Adornment, {
        children: adornmentRight
      })]
    }), description && /*#__PURE__*/jsx(Description, {
      $invalid: invalid,
      id: describedBy,
      $disabled: disabled,
      children: description
    })]
  });
});

export { TextArea };
